import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Platform, Pressable, StyleSheet, Text, TextInput, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { getAuth } from "firebase/auth";
import { collection, deleteDoc, doc, getFirestore, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { app } from "../../firebase";
import AnimatedECardPreview, {
	ECARD_CANONICAL_HEIGHT,
	ECARD_CANONICAL_WIDTH,
	GOOGLE_FONT_OPTIONS,
	TEXT_ANIMATIONS,
	createAnimatedTextLayer,
	ensureGoogleFontLoaded,
	getECardAssetType,
	getECardEmailGifUrl,
	getECardMediaType,
	getECardPlaybackAssetUrl,
	getFontFamilyCss,
	normalizeAnimatedTextLayer,
	normalizeAnimatedTextLayers,
} from "../RegistryWidgets/AnimatedECardPreview";

const buildLayer = (index = 0) => createAnimatedTextLayer({
	id: `layer-${Date.now()}-${index}`,
	name: `Text Layer ${index + 1}`,
	text: index === 0 ? "CONGRATULATIONS!" : "CHEERS TO WHAT'S NEXT",
	x: ECARD_CANONICAL_WIDTH / 2,
	y: ECARD_CANONICAL_HEIGHT * (index === 0 ? 0.42 : 0.58),
	width: ECARD_CANONICAL_WIDTH * 0.78,
	fontSize: ECARD_CANONICAL_WIDTH * (index === 0 ? 34 : 22) / 380,
	startTime: 0,
	endTime: 6,
	endTimeEnabled: false,
	animationDuration: 0.55,
	animationStartX: ECARD_CANONICAL_WIDTH / 2,
	animationStartY: ECARD_CANONICAL_HEIGHT * (index === 0 ? 0.42 : 0.58),
	animationEndX: ECARD_CANONICAL_WIDTH / 2,
	animationEndY: ECARD_CANONICAL_HEIGHT * (index === 0 ? 0.42 : 0.58),
});

const DEFAULT_CARD = {
	title: "",
	assetUrl: "",
	assetPath: "",
	assetType: "video/mp4",
	mediaType: "video",
	videoAssetUrl: "",
	videoAssetPath: "",
	videoAssetType: "video/mp4",
	emailGifUrl: "",
	emailGifPath: "",
	emailGifAssetType: "image/gif",
	published: false,
	type: "animated",
	designCanvas: { width: ECARD_CANONICAL_WIDTH, height: ECARD_CANONICAL_HEIGHT },
	textLayers: [buildLayer(0)],
};

const sanitizeFileName = (name = "ecard-asset") =>
	name.toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/-+/g, "-");

const getStoragePathFromUrl = (url) => {
	if (!url) return "";
	const match = url.match(/\/o\/([^?]+)/);
	return match ? decodeURIComponent(match[1]) : "";
};

const getFileAssetType = (file, expectedType = "") => {
	const fileName = String(file?.name || "").toLowerCase();
	if (fileName.endsWith(".gif")) return "image/gif";
	if (fileName.endsWith(".mp4")) return "video/mp4";
	return file?.type || expectedType;
};

const normalizeDraftAssets = (card = {}) => {
	const playbackUrl = getECardPlaybackAssetUrl(card);
	const playbackType = getECardAssetType(card);
	const playbackMediaType = getECardMediaType(card);
	const videoAssetUrl = card.videoAssetUrl || (playbackMediaType === "video" ? playbackUrl : "");
	const emailGifUrl = getECardEmailGifUrl(card);

	return {
		assetUrl: playbackUrl,
		assetPath: card.videoAssetPath || card.assetPath || "",
		assetType: playbackType,
		mediaType: playbackMediaType,
		videoAssetUrl,
		videoAssetPath: card.videoAssetPath || (playbackMediaType === "video" ? card.assetPath : "") || "",
		videoAssetType: card.videoAssetType || (videoAssetUrl ? "video/mp4" : ""),
		emailGifUrl,
		emailGifPath: card.emailGifPath || (playbackMediaType === "gif" ? card.assetPath : "") || "",
		emailGifAssetType: card.emailGifAssetType || (emailGifUrl ? "image/gif" : ""),
	};
};

const ECardStudio = ({ theme = {} }) => {
	const isWeb = Platform.OS === "web";
	const dbTheme = {
		bgCard: theme.bgCard || "#ffffff",
		textPrimary: theme.textPrimary || "#1f2937",
		textMuted: theme.textMuted || "#6b7280",
		border: theme.border || "#e5e7eb",
		primary: theme.primary || "#4c956c",
	};

	const [cards, setCards] = useState([]);
	const [draft, setDraft] = useState(DEFAULT_CARD);
	const [editingId, setEditingId] = useState(null);
	const [selectedLayerId, setSelectedLayerId] = useState(DEFAULT_CARD.textLayers[0].id);
	const [selectedVideoFile, setSelectedVideoFile] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [gifGeneratingIds, setGifGeneratingIds] = useState({});
	const [status, setStatus] = useState("");
	const [error, setError] = useState("");
	const [fontDropdownOpen, setFontDropdownOpen] = useState(false);
	const [fontSearch, setFontSearch] = useState("");
	const [fontDropdownRect, setFontDropdownRect] = useState(null);
	const fontButtonRef = useRef(null);
	const fontDropdownRef = useRef(null);
	const studioPanelRef = useRef(null);
	const previewColumnRef = useRef(null);
	const previewStickyRef = useRef(null);

	useEffect(() => {
		const unsubscribe = onSnapshot(collection(getFirestore(app), "premiumECards"), (snapshot) => {
			const nextCards = snapshot.docs
				.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
				.filter((card) => card.type === "animated" || card.assetUrl)
				.sort((a, b) => {
					const aTime = a.updatedAt?.seconds || a.createdAt?.seconds || 0;
					const bTime = b.updatedAt?.seconds || b.createdAt?.seconds || 0;
					return bTime - aTime;
				});
			setCards(nextCards);
			setLoading(false);
		}, (listenError) => {
			setError(listenError.message || "Unable to load animated eCards.");
			setLoading(false);
		});

		return unsubscribe;
	}, []);

	const normalizedLayers = useMemo(() => {
		const layers = normalizeAnimatedTextLayers(draft);
		return layers.length ? layers : [buildLayer(0)];
	}, [draft]);

	const previewCard = useMemo(() => ({
		...draft,
		textLayers: normalizedLayers,
	}), [draft, normalizedLayers]);

	const selectedLayer = normalizedLayers.find((layer) => layer.id === selectedLayerId) || normalizedLayers[0];
	const filteredFontOptions = useMemo(() => {
		const query = fontSearch.trim().toLowerCase();
		if (!query) return GOOGLE_FONT_OPTIONS;
		return GOOGLE_FONT_OPTIONS.filter((font) =>
			font.family.toLowerCase().includes(query) ||
			font.category.toLowerCase().includes(query)
		);
	}, [fontSearch]);

	useEffect(() => {
		if (!fontDropdownOpen) return;
		filteredFontOptions.forEach((font) => ensureGoogleFontLoaded(font.family));
	}, [fontDropdownOpen, filteredFontOptions]);

	useEffect(() => {
		if (!isWeb || process.env.NODE_ENV === "production" || typeof window === "undefined" || typeof document === "undefined") return undefined;

		const panelNode = studioPanelRef.current;
		const columnNode = previewColumnRef.current;
		const previewNode = previewStickyRef.current;
		if (!panelNode || !columnNode || !previewNode) return undefined;

		const findScrollElement = (node) => {
			let current = node?.parentElement;
			while (current && current !== document.body && current !== document.documentElement) {
				const style = window.getComputedStyle(current);
				const overflowY = style.overflowY || style.overflow;
				if (/(auto|scroll|overlay)/.test(overflowY)) return current;
				current = current.parentElement;
			}
			return document.scrollingElement || document.documentElement;
		};

		const scrollElement = findScrollElement(panelNode);
		let scrollLogCount = 0;

		const describeNode = (node) => {
			if (node === document.scrollingElement || node === document.documentElement || node === document.body) return "document";
			const tag = node.tagName?.toLowerCase() || "node";
			const className = typeof node.className === "string" && node.className ? `.${node.className.split(/\s+/).join(".")}` : "";
			return `${tag}${className}`;
		};

		const getScrollTop = () => {
			if (scrollElement === document.scrollingElement || scrollElement === document.documentElement || scrollElement === document.body) {
				return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
			}
			return scrollElement.scrollTop;
		};

		const rectSummary = (node) => {
			const rect = node.getBoundingClientRect();
			return {
				top: Math.round(rect.top),
				bottom: Math.round(rect.bottom),
				width: Math.round(rect.width),
				height: Math.round(rect.height),
				scrollHeight: node.scrollHeight,
				clientHeight: node.clientHeight,
			};
		};

		const logStickyDiagnostics = (reason) => {
			const previewStyle = window.getComputedStyle(previewNode);
			console.debug("[ECardStudio sticky diagnostics]", {
				reason,
				scrollElement: describeNode(scrollElement),
				scrollTop: Math.round(getScrollTop()),
				studio: rectSummary(panelNode),
				rightColumn: rectSummary(columnNode),
				preview: rectSummary(previewNode),
				previewPosition: previewStyle.position,
				previewTop: previewStyle.top,
				previewTransform: previewStyle.transform,
			});
		};

		const handleScroll = () => {
			if (scrollLogCount >= 8) return;
			scrollLogCount += 1;
			logStickyDiagnostics("scroll");
		};

		logStickyDiagnostics("mount");
		scrollElement.addEventListener("scroll", handleScroll, { passive: true });

		return () => scrollElement.removeEventListener("scroll", handleScroll);
	}, [isWeb]);

	const updateFontDropdownRect = () => {
		if (!fontButtonRef.current || typeof window === "undefined") return;
		const rect = fontButtonRef.current.getBoundingClientRect();
		setFontDropdownRect({
			top: rect.bottom + 6,
			left: rect.left,
			width: Math.max(rect.width, 360),
		});
	};

	useEffect(() => {
		if (!fontDropdownOpen || typeof window === "undefined") return undefined;

		updateFontDropdownRect();

		const handleViewportChange = () => updateFontDropdownRect();
		const handlePointerDown = (event) => {
			if (fontButtonRef.current?.contains(event.target) || fontDropdownRef.current?.contains(event.target)) return;
			setFontDropdownOpen(false);
		};

		window.addEventListener("resize", handleViewportChange);
		window.addEventListener("scroll", handleViewportChange, true);
		document.addEventListener("pointerdown", handlePointerDown);

		return () => {
			window.removeEventListener("resize", handleViewportChange);
			window.removeEventListener("scroll", handleViewportChange, true);
			document.removeEventListener("pointerdown", handlePointerDown);
		};
	}, [fontDropdownOpen]);

	const resetDraft = () => {
		const fresh = { ...DEFAULT_CARD, textLayers: [buildLayer(0)] };
		setDraft(fresh);
		setEditingId(null);
		setSelectedLayerId(fresh.textLayers[0].id);
		setSelectedVideoFile(null);
		setStatus("");
		setError("");
	};

	const editCard = (card) => {
		const layers = normalizeAnimatedTextLayers(card);
		const nextDraft = {
			...DEFAULT_CARD,
			...card,
			...normalizeDraftAssets(card),
			textLayers: layers.length ? layers : [buildLayer(0)],
		};
		console.log("[ECardStudio] loaded eCard for editing", {
			cardId: card.id,
			videoAssetUrl: nextDraft.videoAssetUrl,
			emailGifUrl: nextDraft.emailGifUrl,
			assetUrl: nextDraft.assetUrl,
			mediaType: nextDraft.mediaType,
		});
		setDraft(nextDraft);
		setEditingId(card.id);
		setSelectedLayerId(nextDraft.textLayers[0]?.id);
		setSelectedVideoFile(null);
		setStatus("");
		setError("");
	};

	const updateDraft = (field, value) => {
		setDraft((current) => ({ ...current, [field]: value }));
	};

	const updateLayer = (layerId, updates) => {
		setDraft((current) => ({
			...current,
			textLayers: normalizeAnimatedTextLayers(current).map((layer) =>
				layer.id === layerId ? normalizeAnimatedTextLayer({ ...layer, ...updates }) : layer
			),
		}));
	};

	const updateLayerPosition = (layerId, position) => {
		const layer = normalizedLayers.find((item) => item.id === layerId);
		updateLayer(layerId, {
			...position,
			...(layer?.animationPositionEnabled ? {
				animationEndX: position.x,
				animationEndY: position.y,
			} : {}),
		});
	};

	const addLayer = () => {
		setDraft((current) => {
			const layers = normalizeAnimatedTextLayers(current);
			const nextLayer = buildLayer(layers.length);
			setSelectedLayerId(nextLayer.id);
			return { ...current, textLayers: [...layers, nextLayer] };
		});
	};

	const removeLayer = (layerId) => {
		setDraft((current) => {
			const layers = normalizeAnimatedTextLayers(current).filter((layer) => layer.id !== layerId);
			const nextLayers = layers.length ? layers : [buildLayer(0)];
			setSelectedLayerId(nextLayers[0].id);
			return { ...current, textLayers: nextLayers };
		});
	};

	const handleVideoFileChange = (event) => {
		const file = event?.target?.files?.[0];
		if (!file) return;

		const inferredAssetType = getFileAssetType(file, "video/mp4");
		if (inferredAssetType !== "video/mp4") {
			setError("Please upload an MP4 file for the full video eCard.");
			return;
		}

		const objectUrl = URL.createObjectURL(file);
		setSelectedVideoFile(file);
		setDraft((current) => ({
			...current,
			assetUrl: objectUrl,
			assetType: inferredAssetType,
			mediaType: "video",
			videoAssetUrl: objectUrl,
			videoAssetType: inferredAssetType,
			emailGifUrl: "",
			emailGifPath: "",
			emailGifGenerationStatus: "pending",
			emailGifGenerationError: "",
		}));
		setError("");
	};

	const generateEmailGif = async (cardId, { force = false } = {}) => {
		if (!cardId) throw new Error("Save the eCard before generating its email GIF.");

		setGifGeneratingIds((current) => ({ ...current, [cardId]: true }));
		try {
			const user = getAuth(app).currentUser;
			const token = await user?.getIdToken();
			if (!token) throw new Error("Please sign in as an admin before generating the email GIF.");

			const { SERVER_URL } = await import("../../config.js");
			const response = await fetch(`${SERVER_URL}/premium-ecards/generate-email-gif`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({ ecardId: cardId, force }),
			});
			const result = await response.json().catch(() => ({}));
			if (!response.ok || result.success === false) {
				throw new Error(result.error || "Unable to generate the email GIF.");
			}
			return result;
		} finally {
			setGifGeneratingIds((current) => {
				const next = { ...current };
				delete next[cardId];
				return next;
			});
		}
	};

	const saveCard = async () => {
		setError("");
		setStatus("");

		if (!draft.title.trim()) {
			setError("Give the eCard a title before saving.");
			return;
		}

		if (!draft.assetUrl && !draft.videoAssetUrl && !selectedVideoFile) {
			setError("Upload a full MP4 video before saving.");
			return;
		}

		setSaving(true);

		try {
			let assetUrl = draft.assetUrl;
			let assetPath = draft.assetPath || "";
			let assetType = getECardAssetType(draft);
			let mediaType = getECardMediaType(draft);
			let videoAssetUrl = draft.videoAssetUrl || "";
			let videoAssetPath = draft.videoAssetPath || "";
			let videoAssetType = draft.videoAssetType || (videoAssetUrl ? "video/mp4" : "");
			let emailGifUrl = draft.emailGifUrl || "";
			let emailGifPath = draft.emailGifPath || "";
			let emailGifAssetType = draft.emailGifAssetType || (emailGifUrl ? "image/gif" : "");

			if (selectedVideoFile) {
				const selectedAssetType = getFileAssetType(selectedVideoFile, "video/mp4");
				const storage = getStorage(app);
				videoAssetPath = `premiumECards/${Date.now()}-${sanitizeFileName(selectedVideoFile.name)}`;
				const uploadRef = ref(storage, videoAssetPath);
				await uploadBytes(uploadRef, selectedVideoFile, { contentType: selectedAssetType });
				videoAssetUrl = await getDownloadURL(uploadRef);
				videoAssetType = selectedAssetType;
				assetUrl = videoAssetUrl;
				assetPath = videoAssetPath;
				assetType = selectedAssetType;
				mediaType = "video";
				emailGifUrl = "";
				emailGifPath = "";
				emailGifAssetType = "image/gif";
			}

			const cardRef = editingId
				? doc(getFirestore(app), "premiumECards", editingId)
				: doc(collection(getFirestore(app), "premiumECards"));
			const shouldGenerateEmailGif = mediaType === "video" && (!!selectedVideoFile || !emailGifUrl);

			console.log("[ECardStudio] saving animated eCard assets", {
				cardId: cardRef.id,
				videoAssetUrl,
				emailGifUrl,
				assetUrl,
				mediaType,
				assetType,
				hasSelectedVideoFile: !!selectedVideoFile,
				shouldGenerateEmailGif,
			});

			await setDoc(cardRef, {
				type: "animated",
				title: draft.title.trim(),
				assetUrl,
				assetPath,
				assetType,
				mediaType,
				videoAssetUrl,
				videoAssetPath,
				videoAssetType,
				emailGifUrl,
				emailGifPath,
				emailGifAssetType,
				emailGifGenerationStatus: shouldGenerateEmailGif ? "pending" : (emailGifUrl ? "ready" : (draft.emailGifGenerationStatus || "")),
				emailGifGenerationError: shouldGenerateEmailGif ? "" : (draft.emailGifGenerationError || ""),
				published: !!draft.published,
				designCanvas: { width: ECARD_CANONICAL_WIDTH, height: ECARD_CANONICAL_HEIGHT },
				designCanvasWidth: ECARD_CANONICAL_WIDTH,
				designCanvasHeight: ECARD_CANONICAL_HEIGHT,
				textLayers: normalizedLayers,
				createdAt: editingId ? (draft.createdAt || serverTimestamp()) : serverTimestamp(),
				updatedAt: serverTimestamp(),
				defaultMessage: "",
				textStyle: null,
			}, { merge: true });

			setEditingId(cardRef.id);
			setSelectedVideoFile(null);
			setDraft((current) => ({ ...current, assetUrl, assetPath, assetType, mediaType, videoAssetUrl, videoAssetPath, videoAssetType, emailGifUrl, emailGifPath, emailGifAssetType, textLayers: normalizedLayers }));
			setStatus(`Full video eCard template saved${shouldGenerateEmailGif ? ". Generating email GIF..." : "."}`);

			if (shouldGenerateEmailGif) {
				try {
					const gifResult = await generateEmailGif(cardRef.id, { force: !!selectedVideoFile });
					setDraft((current) => ({
						...current,
						emailGifUrl: gifResult.emailGifUrl || current.emailGifUrl,
						emailGifPath: gifResult.emailGifPath || current.emailGifPath,
						emailGifAssetType: gifResult.emailGifAssetType || "image/gif",
						emailGifGenerationStatus: gifResult.emailGifGenerationStatus || "ready",
						emailGifGenerationError: "",
					}));
					setStatus("Full video eCard template saved with an automatically generated email GIF.");
				} catch (gifError) {
					setError(gifError.message || "The eCard saved, but the email GIF could not be generated.");
					setStatus("Full video eCard template saved. Email GIF generation needs attention.");
				}
			}
		} catch (saveError) {
			setError(saveError.message || "Unable to save animated eCard.");
		} finally {
			setSaving(false);
		}
	};

	const togglePublished = async (card) => {
		try {
			const nextPublished = !card.published;
			await setDoc(doc(getFirestore(app), "premiumECards", card.id), {
				published: nextPublished,
				updatedAt: serverTimestamp(),
			}, { merge: true });
			if (nextPublished && getECardMediaType(card) === "video" && !getECardEmailGifUrl(card)) {
				setStatus(`Published "${card.title || "animated eCard"}". Generating email GIF...`);
				try {
					await generateEmailGif(card.id, { force: false });
					setStatus(`Published "${card.title || "animated eCard"}" with an automatically generated email GIF.`);
				} catch (gifError) {
					setError(gifError.message || "Published eCard, but email GIF generation failed.");
					setStatus(`Published "${card.title || "animated eCard"}". Email GIF generation needs attention.`);
				}
			}
		} catch (publishError) {
			setError(publishError.message || "Unable to update publish status.");
		}
	};

	const deleteCard = async (card) => {
		const confirmed = window.confirm(`Delete "${card.title || "this animated eCard"}"? This removes it from the user-facing card selector.`);
		if (!confirmed) return;

		setError("");
		setStatus("");

		try {
			await deleteDoc(doc(getFirestore(app), "premiumECards", card.id));

			const storagePaths = Array.from(new Set([
				card.assetPath || getStoragePathFromUrl(card.assetUrl),
				card.videoAssetPath || getStoragePathFromUrl(card.videoAssetUrl),
				card.emailGifPath || getStoragePathFromUrl(card.emailGifUrl),
			].filter(Boolean)));
			for (const storagePath of storagePaths) {
				try {
					await deleteObject(ref(getStorage(app), storagePath));
				} catch (storageError) {
					console.warn("Animated eCard media delete failed", storageError);
					if (editingId === card.id) resetDraft();
					setStatus("Animated eCard deleted. Media cleanup could not be completed automatically.");
					return;
				}
			}

			if (editingId === card.id) resetDraft();
			setStatus("Animated eCard deleted.");
		} catch (deleteError) {
			setError(deleteError.message || "Unable to delete animated eCard.");
		}
	};

	const updateNumericLayerField = (field, value) => {
		const numericValue = Number(value);
		const syncedUpdates = {};
		if (selectedLayer.animationPositionEnabled && field === "x") syncedUpdates.animationEndX = numericValue;
		if (selectedLayer.animationPositionEnabled && field === "y") syncedUpdates.animationEndY = numericValue;
		if (selectedLayer.animationPositionEnabled && field === "animationEndX") syncedUpdates.x = numericValue;
		if (selectedLayer.animationPositionEnabled && field === "animationEndY") syncedUpdates.y = numericValue;
		updateLayer(selectedLayer.id, { [field]: numericValue, ...syncedUpdates });
	};

	const renderRange = (label, field, min, max, step = 1, unit = "") => (
		<View style={styles.controlGroup}>
			<View style={styles.controlHeader}>
				<Text style={[styles.label, { color: dbTheme.textPrimary }]}>{label}</Text>
			</View>
			<View style={styles.rangeRow}>
				{React.createElement("input", {
					type: "range",
					min,
					max,
					step,
					value: selectedLayer[field],
					onChange: (event) => updateNumericLayerField(field, event.target.value),
					style: styles.webRange,
				})}
				{React.createElement("input", {
					type: "number",
					min,
					max,
					step,
					value: selectedLayer[field],
					onChange: (event) => updateNumericLayerField(field, event.target.value),
					style: styles.numberInput,
				})}
				{!!unit && <Text style={[styles.unitText, { color: dbTheme.textMuted }]}>{unit}</Text>}
			</View>
		</View>
	);

	const selectFont = (fontFamily) => {
		if (!fontFamily.trim()) return;
		ensureGoogleFontLoaded(fontFamily);
		updateLayer(selectedLayer.id, { fontFamily });
		setFontSearch("");
		setFontDropdownOpen(false);
	};

	const renderFontDropdown = () => {
		if (!fontDropdownOpen || typeof document === "undefined" || !fontDropdownRect) return null;

		return createPortal(
			React.createElement("div", {
				ref: fontDropdownRef,
				style: {
					...styles.fontDropdown,
					top: fontDropdownRect.top,
					left: fontDropdownRect.left,
					width: fontDropdownRect.width,
				},
			},
				React.createElement("input", {
					type: "search",
					value: fontSearch,
					placeholder: "Search fonts...",
					autoFocus: true,
					onChange: (event) => setFontSearch(event.target.value),
					style: styles.fontSearchInput,
				}),
				React.createElement("div", { style: styles.fontOptionsList },
					filteredFontOptions.length === 0
						? React.createElement("button", {
							type: "button",
							onClick: () => selectFont(fontSearch),
							style: styles.fontOption,
						}, `Use "${fontSearch}"`)
						: filteredFontOptions.map((font) => React.createElement("button", {
							key: font.family,
							type: "button",
							onMouseEnter: () => ensureGoogleFontLoaded(font.family),
							onClick: () => selectFont(font.family),
							style: {
								...styles.fontOption,
								...(selectedLayer.fontFamily === font.family ? styles.fontOptionSelected : {}),
							},
						}, [
							React.createElement("span", {
								key: "family",
								style: {
									...styles.fontOptionFamily,
									fontFamily: getFontFamilyCss(font.family),
								},
							}, font.family),
							React.createElement("span", { key: "category", style: styles.fontOptionCategory }, font.category),
						]))
				),
				fontSearch.trim().length > 0 && !GOOGLE_FONT_OPTIONS.some((font) => font.family.toLowerCase() === fontSearch.trim().toLowerCase()) && React.createElement("button", {
					type: "button",
					onClick: () => selectFont(fontSearch.trim()),
					style: styles.customFontButton,
				}, `Use custom font family: ${fontSearch.trim()}`)
			),
			document.body
		);
	};

	const renderSelect = (label, field, options) => (
		<View style={styles.controlGroup}>
			<Text style={[styles.label, { color: dbTheme.textPrimary }]}>{label}</Text>
			{React.createElement("select", {
				value: selectedLayer[field],
				onChange: (event) => updateLayer(selectedLayer.id, { [field]: event.target.value }),
				style: styles.selectInput,
			}, options.map((option) => React.createElement("option", {
				key: option.value,
				value: option.value,
			}, option.label)))}
		</View>
	);

	const toggleAnimationPosition = (enabled) => {
		updateLayer(selectedLayer.id, {
			animationPositionEnabled: enabled,
			animationStartX: enabled ? Number(selectedLayer.animationStartX ?? selectedLayer.x) : selectedLayer.animationStartX,
			animationStartY: enabled ? Number(selectedLayer.animationStartY ?? selectedLayer.y) : selectedLayer.animationStartY,
			animationEndX: enabled ? Number(selectedLayer.animationEndX ?? selectedLayer.x) : selectedLayer.animationEndX,
			animationEndY: enabled ? Number(selectedLayer.animationEndY ?? selectedLayer.y) : selectedLayer.animationEndY,
		});
	};

	const StudioPanel = isWeb ? "section" : View;
	const StudioGrid = isWeb ? "div" : View;
	const PreviewColumn = isWeb ? "aside" : View;
	const PreviewSticky = isWeb ? "div" : View;

	const studioPanelProps = isWeb
		? {
			ref: studioPanelRef,
			style: StyleSheet.flatten([styles.panel, styles.webPanel, { backgroundColor: dbTheme.bgCard, borderColor: dbTheme.border }]),
		}
		: { style: [styles.panel, { backgroundColor: dbTheme.bgCard, borderColor: dbTheme.border }] };

	return (
		<View style={styles.container}>
			<StudioPanel {...studioPanelProps}>
				<View style={styles.headerRow}>
					<View>
						<Text style={[styles.title, { color: dbTheme.textPrimary }]}>Premium eCard Studio</Text>
						<Text style={[styles.subtitle, { color: dbTheme.textMuted }]}>Build reusable animated-card templates with timed, customizable text layers.</Text>
					</View>
					<Pressable style={[styles.secondaryButton, { borderColor: dbTheme.border }]} onPress={resetDraft}>
						<Ionicons name="add-circle-outline" size={18} color={dbTheme.primary} />
						<Text style={[styles.secondaryButtonText, { color: dbTheme.primary }]}>New Card</Text>
					</Pressable>
				</View>

				<StudioGrid style={isWeb ? styles.studioGridWeb : styles.studioGrid}>
					<View style={styles.formColumn}>
						<View style={styles.fieldGroup}>
							<Text style={[styles.label, { color: dbTheme.textPrimary }]}>Title</Text>
							<TextInput
								style={[styles.input, { borderColor: dbTheme.border, color: dbTheme.textPrimary }]}
								value={draft.title}
								onChangeText={(value) => updateDraft("title", value)}
								placeholder="Confetti Graduate"
								placeholderTextColor="#9ca3af"
							/>
						</View>

						<View style={styles.mediaAssetGrid}>
							<View style={styles.mediaAssetBox}>
								<Text style={[styles.label, { color: dbTheme.textPrimary }]}>Full Video (MP4)</Text>
								{React.createElement("input", {
									type: "file",
									accept: "video/mp4",
									onChange: handleVideoFileChange,
									style: styles.fileInput,
								})}
								<Text style={[styles.helperText, { color: dbTheme.textMuted }]}>
									Main eCard for previews and the hosted viewer. Audio is preserved.
								</Text>
								{!!(draft.videoAssetUrl || (getECardMediaType(draft) === "video" && draft.assetUrl)) && (
									<Text style={styles.assetStatusText}>Full video attached</Text>
								)}
							</View>
							<View style={styles.mediaAssetBox}>
								<Text style={[styles.label, { color: dbTheme.textPrimary }]}>Automatic Email GIF</Text>
								<Text style={[styles.helperText, { color: dbTheme.textMuted }]}>
									Generated from the MP4 after save. This is the animated image used inside emails.
								</Text>
								{!!draft.emailGifUrl && (
									<Text style={styles.assetStatusText}>Email GIF generated</Text>
								)}
								{draft.emailGifGenerationStatus === "generating" && (
									<Text style={styles.pendingStatusText}>Generating email GIF...</Text>
								)}
								{draft.emailGifGenerationStatus === "error" && (
									<Text style={styles.errorText}>{draft.emailGifGenerationError || "Email GIF generation failed."}</Text>
								)}
							</View>
						</View>

						<View style={styles.toggleRow}>
							{React.createElement("input", {
								type: "checkbox",
								checked: !!draft.published,
								onChange: (event) => updateDraft("published", event.target.checked),
							})}
							<Text style={[styles.label, { color: dbTheme.textPrimary }]}>Published and available to users</Text>
						</View>

						<View style={styles.layerHeaderRow}>
							<Text style={[styles.sectionTitle, { color: dbTheme.textPrimary }]}>Text Layers</Text>
							<Pressable style={[styles.secondaryButton, { borderColor: dbTheme.border }]} onPress={addLayer}>
								<Ionicons name="add-outline" size={16} color={dbTheme.primary} />
								<Text style={[styles.secondaryButtonText, { color: dbTheme.primary }]}>Add Text Layer</Text>
							</Pressable>
						</View>

						<View style={styles.layerTabs}>
							{normalizedLayers.map((layer, index) => (
								<Pressable
									key={layer.id}
									style={[
										styles.layerTab,
										{ borderColor: dbTheme.border },
										selectedLayer.id === layer.id && styles.layerTabActive,
									]}
									onPress={() => setSelectedLayerId(layer.id)}
								>
									<Text style={[styles.layerTabText, selectedLayer.id === layer.id && styles.layerTabTextActive]}>
										{layer.name || `Layer ${index + 1}`}
									</Text>
								</Pressable>
							))}
						</View>

						<View style={styles.layerEditor}>
							<View style={styles.fieldGroup}>
								<Text style={[styles.label, { color: dbTheme.textPrimary }]}>Layer Name</Text>
								<TextInput
									style={[styles.input, { borderColor: dbTheme.border, color: dbTheme.textPrimary }]}
									value={selectedLayer.name}
									onChangeText={(value) => updateLayer(selectedLayer.id, { name: value })}
								/>
							</View>

							<View style={styles.fieldGroup}>
								<Text style={[styles.label, { color: dbTheme.textPrimary }]}>Default/Example Text</Text>
								<TextInput
									style={[styles.input, styles.textArea, { borderColor: dbTheme.border, color: dbTheme.textPrimary }]}
									value={selectedLayer.text}
									onChangeText={(value) => updateLayer(selectedLayer.id, { text: value })}
									multiline
								/>
							</View>

							<View style={styles.toggleRow}>
								{React.createElement("input", {
									type: "checkbox",
									checked: !!selectedLayer.allowUserPersonalization,
									onChange: (event) => updateLayer(selectedLayer.id, { allowUserPersonalization: event.target.checked }),
								})}
								<Text style={[styles.label, { color: dbTheme.textPrimary }]}>Allow user to personalize this text</Text>
							</View>

							{renderRange("Horizontal Position", "x", 0, ECARD_CANONICAL_WIDTH, 1, "px")}
							{renderRange("Vertical Position", "y", 0, ECARD_CANONICAL_HEIGHT, 1, "px")}
							{renderRange("Text Box Width", "width", ECARD_CANONICAL_WIDTH * 0.12, ECARD_CANONICAL_WIDTH, 1, "px")}
							{renderRange("Font Size", "fontSize", 16, 260, 1, "px")}
							{renderRange("Line Height", "lineHeight", 0.8, 2, 0.05)}
							{renderRange("Letter Spacing", "letterSpacing", -8, 40, 0.5, "px")}
							{renderRange("Start Time", "startTime", 0, 30, 0.1, "s")}

							<View style={styles.toggleRow}>
								{React.createElement("input", {
									type: "checkbox",
									checked: selectedLayer.endTimeEnabled === false,
									onChange: (event) => updateLayer(selectedLayer.id, { endTimeEnabled: !event.target.checked }),
								})}
								<Text style={[styles.label, { color: dbTheme.textPrimary }]}>No end time / Stay visible</Text>
							</View>
							{selectedLayer.endTimeEnabled !== false && renderRange("End Time", "endTime", 0.1, 60, 0.1, "s")}

							<View style={styles.controlGrid}>
								<View style={[styles.controlGroup, styles.fontControlGroup]}>
									<Text style={[styles.label, { color: dbTheme.textPrimary }]}>Font Family</Text>
									{React.createElement("div", { style: styles.fontPicker },
										React.createElement("button", {
											ref: fontButtonRef,
											type: "button",
											onClick: () => {
												setFontDropdownOpen((open) => {
													const nextOpen = !open;
													if (nextOpen) setTimeout(updateFontDropdownRect, 0);
													return nextOpen;
												});
												setFontSearch("");
											},
											style: styles.fontPickerButton,
										}, [
											React.createElement("span", {
												key: "label",
												style: {
													...styles.fontPickerButtonText,
													fontFamily: getFontFamilyCss(selectedLayer.fontFamily),
												},
											}, selectedLayer.fontFamily || "Choose a font"),
											React.createElement("span", { key: "icon", style: styles.fontPickerChevron }, fontDropdownOpen ? "▲" : "▼"),
										]),
										renderFontDropdown()
									)}
									<Text style={[styles.helperText, { color: dbTheme.textMuted }]}>Choose a Google Font from the list, or search and use a custom fallback family.</Text>
								</View>
								<View style={styles.controlGroup}>
									<Text style={[styles.label, { color: dbTheme.textPrimary }]}>Font Weight</Text>
									<TextInput
										style={[styles.input, { borderColor: dbTheme.border, color: dbTheme.textPrimary }]}
										value={String(selectedLayer.fontWeight)}
										onChangeText={(value) => updateLayer(selectedLayer.id, { fontWeight: value })}
									/>
								</View>
							</View>

							<View style={styles.controlGrid}>
								<View style={styles.controlGroup}>
									<Text style={[styles.label, { color: dbTheme.textPrimary }]}>Text Color</Text>
									{React.createElement("input", {
										type: "color",
										value: selectedLayer.color,
										onChange: (event) => updateLayer(selectedLayer.id, { color: event.target.value }),
										style: styles.colorInput,
									})}
								</View>
								<View style={styles.controlGroup}>
									<Text style={[styles.label, { color: dbTheme.textPrimary }]}>Stroke Color</Text>
									{React.createElement("input", {
										type: "color",
										value: selectedLayer.strokeColor?.startsWith("#") ? selectedLayer.strokeColor : "#000000",
										onChange: (event) => updateLayer(selectedLayer.id, { strokeColor: event.target.value }),
										style: styles.colorInput,
									})}
								</View>
							</View>

							{renderRange("Stroke Width", "strokeWidth", 0, 14, 0.25, "px")}
							{renderRange("Shadow Blur", "textShadowBlur", 0, 72, 1, "px")}

							<View style={styles.toggleRow}>
								{React.createElement("input", {
									type: "checkbox",
									checked: !!selectedLayer.textShadowEnabled,
									onChange: (event) => updateLayer(selectedLayer.id, { textShadowEnabled: event.target.checked }),
								})}
								<Text style={[styles.label, { color: dbTheme.textPrimary }]}>Text shadow enabled</Text>
							</View>

							{renderSelect("Alignment", "textAlign", [
								{ value: "center", label: "Center" },
								{ value: "left", label: "Left" },
								{ value: "right", label: "Right" },
							])}
							{renderSelect("Entrance Animation", "entranceAnimation", TEXT_ANIMATIONS)}
							{renderSelect("Exit Animation", "exitAnimation", TEXT_ANIMATIONS)}

							<View style={styles.motionPathPanel}>
								<View style={styles.toggleRow}>
									{React.createElement("input", {
										type: "checkbox",
										checked: !!selectedLayer.animationPositionEnabled,
										onChange: (event) => toggleAnimationPosition(event.target.checked),
									})}
									<Text style={[styles.label, { color: dbTheme.textPrimary }]}>Use custom animation start/end position</Text>
								</View>
								{selectedLayer.animationPositionEnabled && (
									<View>
										<Text style={[styles.helperText, { color: dbTheme.textMuted }]}>The layer rests at the end position. Slide-style presets and fades still apply.</Text>
										{renderRange("Animation Duration", "animationDuration", 0.1, 10, 0.1, "s")}
										<View style={styles.controlGrid}>
											{renderRange("Start X", "animationStartX", 0, ECARD_CANONICAL_WIDTH, 1, "px")}
											{renderRange("Start Y", "animationStartY", 0, ECARD_CANONICAL_HEIGHT, 1, "px")}
										</View>
										<View style={styles.controlGrid}>
											{renderRange("End X", "animationEndX", 0, ECARD_CANONICAL_WIDTH, 1, "px")}
											{renderRange("End Y", "animationEndY", 0, ECARD_CANONICAL_HEIGHT, 1, "px")}
										</View>
									</View>
								)}
							</View>

							<View style={styles.actionsRow}>
								<Pressable style={[styles.dangerButton, normalizedLayers.length === 1 && styles.disabledButton]} onPress={() => removeLayer(selectedLayer.id)} disabled={normalizedLayers.length === 1}>
									<Ionicons name="trash-outline" size={16} color="#ffffff" />
									<Text style={styles.primaryButtonText}>Remove Layer</Text>
								</Pressable>
							</View>
						</View>

						<View style={styles.actionsRow}>
							<Pressable style={[styles.primaryButton, saving && styles.disabledButton]} onPress={saveCard} disabled={saving}>
								<Ionicons name="save-outline" size={18} color="#ffffff" />
								<Text style={styles.primaryButtonText}>{saving ? "Saving..." : "Save Animated eCard"}</Text>
							</Pressable>
						</View>

						{!!status && <Text style={styles.statusText}>{status}</Text>}
						{!!error && <Text style={styles.errorText}>{error}</Text>}
					</View>

					<PreviewColumn ref={isWeb ? previewColumnRef : undefined} style={isWeb ? styles.previewColumnWeb : styles.previewColumn}>
						<PreviewSticky ref={isWeb ? previewStickyRef : undefined} style={isWeb ? styles.previewStickyWeb : styles.previewSticky}>
							<View style={styles.previewHeader}>
								<Text style={[styles.sectionTitle, { color: dbTheme.textPrimary }]}>Admin Preview</Text>
								<Text style={[styles.helperText, styles.previewHelperText, { color: dbTheme.textMuted }]}>Drag a layer into preview for quick positioning, then fine-tune with the controls.</Text>
							</View>
							<View style={styles.previewShell}>
								<AnimatedECardPreview
									card={previewCard}
									style={styles.previewFrame}
									editable
									selectedLayerId={selectedLayer?.id}
									onSelectLayer={setSelectedLayerId}
									onLayerPositionChange={updateLayerPosition}
								/>
							</View>
						</PreviewSticky>
					</PreviewColumn>
				</StudioGrid>
			</StudioPanel>

			<View style={[styles.panel, { backgroundColor: dbTheme.bgCard, borderColor: dbTheme.border }]}>
				<Text style={[styles.sectionTitle, { color: dbTheme.textPrimary }]}>Animated eCards</Text>
				{loading ? (
					<Text style={[styles.helperText, { color: dbTheme.textMuted }]}>Loading animated eCards...</Text>
				) : cards.length === 0 ? (
					<Text style={[styles.helperText, { color: dbTheme.textMuted }]}>No animated eCards have been created yet.</Text>
				) : (
					<View style={styles.cardList}>
						{cards.map((card) => {
							const layers = normalizeAnimatedTextLayers(card);
							const emailGifUrl = getECardEmailGifUrl(card);
							const isVideoCard = getECardMediaType(card) === "video";
							const isGeneratingGif = !!gifGeneratingIds[card.id] || card.emailGifGenerationStatus === "generating";
							const gifStatus = isGeneratingGif
								? "Generating email GIF"
								: emailGifUrl
									? "Email GIF ready"
									: card.emailGifGenerationStatus === "error"
										? "Email GIF error"
										: "No email GIF";
							return (
								<View key={card.id} style={[styles.cardRow, { borderColor: dbTheme.border }]}>
									<AnimatedECardPreview card={{ ...card, textLayers: layers }} style={styles.rowPreview} />
									<View style={styles.cardRowBody}>
										<Text style={[styles.cardTitle, { color: dbTheme.textPrimary }]}>{card.title}</Text>
										<Text style={[styles.cardMeta, { color: dbTheme.textMuted }]}>
											{isVideoCard ? "Full video" : "GIF-only"} - {gifStatus} - {layers.length} layer{layers.length === 1 ? "" : "s"} - {card.published ? "Published" : "Draft"}
										</Text>
										{card.emailGifGenerationStatus === "error" && !!card.emailGifGenerationError && (
											<Text style={styles.cardErrorText}>{card.emailGifGenerationError}</Text>
										)}
									</View>
									<Pressable style={[styles.secondaryButton, { borderColor: dbTheme.border }]} onPress={() => editCard(card)}>
										<Ionicons name="create-outline" size={16} color={dbTheme.primary} />
										<Text style={[styles.secondaryButtonText, { color: dbTheme.primary }]}>Edit</Text>
									</Pressable>
									{isVideoCard && !emailGifUrl && (
										<Pressable
											style={[styles.secondaryButton, { borderColor: dbTheme.border }, isGeneratingGif && styles.disabledButton]}
											onPress={async () => {
												setError("");
												setStatus("");
												try {
													await generateEmailGif(card.id, { force: false });
													setStatus(`Generated email GIF for "${card.title || "animated eCard"}".`);
												} catch (gifError) {
													setError(gifError.message || "Unable to generate email GIF.");
												}
											}}
											disabled={isGeneratingGif}
										>
											<Ionicons name="film-outline" size={16} color={dbTheme.primary} />
											<Text style={[styles.secondaryButtonText, { color: dbTheme.primary }]}>{isGeneratingGif ? "Generating" : "Generate GIF"}</Text>
										</Pressable>
									)}
									<Pressable style={[styles.publishButton, card.published ? styles.unpublishButton : styles.publishActiveButton]} onPress={() => togglePublished(card)}>
										<Text style={styles.publishButtonText}>{card.published ? "Unpublish" : "Publish"}</Text>
									</Pressable>
									<Pressable style={styles.deleteCardButton} onPress={() => deleteCard(card)}>
										<Ionicons name="trash-outline" size={16} color="#ffffff" />
										<Text style={styles.publishButtonText}>Delete</Text>
									</Pressable>
								</View>
							);
						})}
					</View>
				)}
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		width: "100%",
		gap: 20,
		overflow: "visible",
	},
	panel: {
		borderWidth: 1,
		borderRadius: 12,
		padding: 20,
		overflow: "visible",
	},
	webPanel: {
		display: "block",
		boxSizing: "border-box",
		borderStyle: "solid",
	},
	headerRow: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "flex-start",
		gap: 16,
		marginBottom: 20,
	},
	title: { fontSize: 24, fontWeight: "700" },
	subtitle: { fontSize: 14, marginTop: 4 },
	studioGrid: { flexDirection: "row", gap: 24, flexWrap: "wrap" },
	studioGridWeb: {
		display: "flex",
		flexDirection: "row",
		alignItems: "stretch",
		gap: 24,
		flexWrap: "wrap",
		overflow: "visible",
	},
	formColumn: { flex: 1, minWidth: 340 },
	previewColumn: {
		width: 500,
		maxWidth: "100%",
		zIndex: 1,
		alignSelf: "stretch",
		overflow: "visible",
	},
	previewColumnWeb: {
		flex: "0 0 500px",
		maxWidth: "100%",
		alignSelf: "stretch",
		position: "relative",
		zIndex: 1,
		overflow: "visible",
	},
	previewSticky: {
		position: "relative",
		alignSelf: "flex-start",
		width: "100%",
		zIndex: 2,
	},
	previewStickyWeb: {
		position: "sticky",
		top: 16,
		alignSelf: "flex-start",
		width: "100%",
		zIndex: 2,
	},
	previewHeader: {
		width: "100%",
		marginBottom: 12,
	},
	previewHelperText: {
		display: "block",
		marginBottom: 0,
	},
	fieldGroup: { marginBottom: 16 },
	controlGroup: { flex: 1, marginBottom: 14, overflow: "visible" },
	fontControlGroup: {
		position: "relative",
		zIndex: 100000,
	},
	controlHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
	},
	controlGrid: {
		flexDirection: "row",
		gap: 12,
		flexWrap: "wrap",
		overflow: "visible",
	},
	label: {
		fontSize: 14,
		fontWeight: "600",
		marginBottom: 8,
	},
	input: {
		backgroundColor: "#ffffff",
		borderWidth: 1,
		borderRadius: 8,
		padding: 12,
		fontSize: 15,
	},
	textArea: {
		height: 88,
		verticalAlign: "top",
	},
	fileInput: {
		width: "100%",
		padding: 10,
		border: "1px solid #e5e7eb",
		borderRadius: 8,
		background: "#ffffff",
	},
	mediaAssetGrid: {
		flexDirection: "row",
		gap: 12,
		flexWrap: "wrap",
		marginBottom: 16,
	},
	mediaAssetBox: {
		flex: 1,
		minWidth: 240,
		borderWidth: 1,
		borderColor: "#e5e7eb",
		borderRadius: 10,
		padding: 12,
		backgroundColor: "#f8fafc",
	},
	assetStatusText: {
		color: "#15803d",
		fontSize: 12,
		fontWeight: "800",
		marginTop: 2,
	},
	pendingStatusText: {
		color: "#b45309",
		fontSize: 12,
		fontWeight: "800",
		marginTop: 2,
	},
	helperText: {
		fontSize: 13,
		lineHeight: 18,
		marginTop: 4,
		marginBottom: 12,
	},
	toggleRow: {
		flexDirection: "row",
		alignItems: "center",
		gap: 10,
		marginBottom: 16,
	},
	layerHeaderRow: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 12,
		marginTop: 8,
		marginBottom: 12,
	},
	sectionTitle: {
		fontSize: 18,
		fontWeight: "700",
		marginBottom: 12,
	},
	layerTabs: {
		flexDirection: "row",
		flexWrap: "wrap",
		gap: 8,
		marginBottom: 14,
	},
	layerTab: {
		borderWidth: 1,
		borderRadius: 999,
		paddingVertical: 8,
		paddingHorizontal: 12,
		backgroundColor: "#ffffff",
	},
	layerTabActive: {
		backgroundColor: "#e8f0e5",
		borderColor: "#4c956c",
	},
	layerTabText: {
		color: "#4b5563",
		fontWeight: "700",
		fontSize: 12,
	},
	layerTabTextActive: {
		color: "#4c956c",
	},
	layerEditor: {
		position: "relative",
		zIndex: 10,
		overflow: "visible",
		borderWidth: 1,
		borderColor: "#e5e7eb",
		borderRadius: 12,
		padding: 14,
		marginBottom: 16,
		backgroundColor: "#f8fafc",
	},
	webRange: { width: "100%" },
	rangeRow: {
		flexDirection: "row",
		alignItems: "center",
		gap: 10,
	},
	numberInput: {
		width: 76,
		padding: 8,
		border: "1px solid #d1d5db",
		borderRadius: 8,
		background: "#ffffff",
		fontSize: 14,
	},
	unitText: {
		minWidth: 20,
		fontSize: 12,
		fontWeight: "700",
	},
	webTextInput: {
		width: "100%",
		padding: 12,
		border: "1px solid #e5e7eb",
		borderRadius: 8,
		background: "#ffffff",
		fontSize: 15,
		boxSizing: "border-box",
	},
	fontPicker: {
		position: "relative",
		zIndex: 100001,
	},
	fontPickerButton: {
		width: "100%",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 10,
		padding: "11px 12px",
		border: "1px solid #e5e7eb",
		borderRadius: 8,
		background: "#ffffff",
		cursor: "pointer",
		textAlign: "left",
		boxSizing: "border-box",
	},
	fontPickerButtonText: {
		flex: 1,
		color: "#1f2937",
		fontSize: 16,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	fontPickerChevron: {
		color: "#6b7280",
		fontSize: 11,
		fontWeight: "700",
	},
	fontDropdown: {
		position: "fixed",
		zIndex: 10000,
		backgroundColor: "#ffffff",
		background: "#ffffff",
		border: "1px solid #94a3b8",
		borderRadius: 10,
		boxShadow: "0 22px 48px rgba(15, 23, 42, 0.34)",
		padding: 10,
		boxSizing: "border-box",
		minWidth: 320,
		maxWidth: "min(520px, calc(100vw - 24px))",
	},
	fontSearchInput: {
		width: "100%",
		padding: "10px 12px",
		border: "1px solid #e5e7eb",
		borderRadius: 8,
		fontSize: 14,
		marginBottom: 8,
		boxSizing: "border-box",
	},
	fontOptionsList: {
		maxHeight: 280,
		overflowY: "auto",
		display: "flex",
		flexDirection: "column",
		gap: 4,
		backgroundColor: "#ffffff",
	},
	fontOption: {
		width: "100%",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 10,
		padding: "9px 10px",
		border: "1px solid transparent",
		borderRadius: 8,
		background: "#ffffff",
		cursor: "pointer",
		textAlign: "left",
	},
	fontOptionSelected: {
		background: "#e8f0e5",
		borderColor: "#4c956c",
	},
	fontOptionFamily: {
		color: "#111827",
		fontSize: 20,
		lineHeight: 1.1,
	},
	fontOptionCategory: {
		color: "#6b7280",
		fontSize: 11,
		fontWeight: "700",
		textTransform: "uppercase",
		whiteSpace: "nowrap",
	},
	customFontButton: {
		width: "100%",
		marginTop: 8,
		padding: "9px 10px",
		border: "1px dashed #94a3b8",
		borderRadius: 8,
		background: "#f8fafc",
		color: "#334155",
		cursor: "pointer",
		textAlign: "left",
		fontWeight: "700",
	},
	colorInput: {
		width: 52,
		height: 36,
		border: "1px solid #e5e7eb",
		borderRadius: 6,
		background: "#ffffff",
	},
	selectInput: {
		width: "100%",
		padding: 10,
		border: "1px solid #e5e7eb",
		borderRadius: 8,
		background: "#ffffff",
	},
	motionPathPanel: {
		borderWidth: 1,
		borderColor: "#e5e7eb",
		borderRadius: 10,
		padding: 12,
		marginTop: 4,
		marginBottom: 12,
		backgroundColor: "#ffffff",
	},
	actionsRow: {
		flexDirection: "row",
		marginTop: 8,
		gap: 10,
		flexWrap: "wrap",
	},
	primaryButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		backgroundColor: "#4c956c",
		borderRadius: 8,
		paddingVertical: 12,
		paddingHorizontal: 16,
	},
	dangerButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		backgroundColor: "#dc2626",
		borderRadius: 8,
		paddingVertical: 10,
		paddingHorizontal: 14,
	},
	primaryButtonText: {
		color: "#ffffff",
		fontWeight: "700",
	},
	secondaryButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 6,
		borderWidth: 1,
		borderRadius: 8,
		paddingVertical: 9,
		paddingHorizontal: 12,
		backgroundColor: "#ffffff",
	},
	secondaryButtonText: {
		fontWeight: "700",
		fontSize: 13,
	},
	disabledButton: { opacity: 0.55 },
	statusText: {
		color: "#15803d",
		fontWeight: "600",
		marginTop: 12,
	},
	errorText: {
		color: "#dc2626",
		fontWeight: "600",
		marginTop: 12,
	},
	previewShell: {
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "#0f172a",
		borderRadius: 12,
		padding: 12,
	},
	previewFrame: {
		width: "100%",
		maxWidth: 500,
		aspectRatio: 1,
		borderRadius: 10,
	},
	cardList: { gap: 12 },
	cardRow: {
		flexDirection: "row",
		alignItems: "center",
		gap: 14,
		borderWidth: 1,
		borderRadius: 10,
		padding: 12,
		backgroundColor: "#ffffff",
		flexWrap: "wrap",
	},
	rowPreview: {
		width: 136,
		height: 136,
		aspectRatio: 1,
		borderRadius: 8,
	},
	cardRowBody: { flex: 1, minWidth: 180 },
	cardTitle: { fontSize: 16, fontWeight: "700" },
	cardMeta: { fontSize: 13, marginTop: 4 },
	cardErrorText: {
		color: "#b91c1c",
		fontSize: 12,
		fontWeight: "700",
		marginTop: 4,
	},
	publishButton: {
		borderRadius: 8,
		paddingVertical: 10,
		paddingHorizontal: 12,
	},
	publishActiveButton: { backgroundColor: "#4c956c" },
	unpublishButton: { backgroundColor: "#ef4444" },
	deleteCardButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 6,
		borderRadius: 8,
		paddingVertical: 10,
		paddingHorizontal: 12,
		backgroundColor: "#991b1b",
	},
	publishButtonText: {
		color: "#ffffff",
		fontWeight: "700",
		fontSize: 13,
	},
});

export default ECardStudio;
